import type { FramingSceneView } from "../framingDiagnostics";
import type { FramingSectionModel } from "./framingSectionModel";

export type CropValidationStatus = "ready" | "attention" | "incomplete";

export type CropValidationCheck = {
  id: "plan" | "receipt" | "qc";
  label: string;
  complete: number;
  total: number;
};

export type CropValidationModel = {
  status: CropValidationStatus;
  checks: CropValidationCheck[];
  failedQc: number;
  planAppliedMismatches: number;
  fallbackOrRepair: number;
  cropFeedback: number;
  uncorrelatedFeedback: number;
  firstRiskSceneId: string | null;
};

export function buildCropValidationModel(
  scenes: readonly FramingSceneView[],
  section: FramingSectionModel,
): CropValidationModel {
  const total = scenes.length;
  const checks: CropValidationCheck[] = [
    {
      id: "plan",
      label: "Canonical plan captured",
      complete: scenes.filter((scene) => scene.planDigest != null).length,
      total,
    },
    {
      id: "receipt",
      label: "Applied render receipt captured",
      complete: scenes.filter(
        (scene) => scene.receiptId != null || scene.appliedStrategy != null,
      ).length,
      total,
    },
    {
      id: "qc",
      label: "Encoded-delivery QC captured",
      complete: scenes.filter((scene) => scene.qc != null).length,
      total,
    },
  ];
  const failedQc = scenes.filter(
    (scene) => scene.qc?.status === "failed",
  ).length;
  const planAppliedMismatches = scenes.filter(
    (scene) =>
      scene.plannedStrategy != null &&
      scene.appliedStrategy != null &&
      scene.plannedStrategy !== scene.appliedStrategy,
  ).length;
  const fallbackOrRepair = scenes.filter(
    (scene) => scene.fallback || scene.repaired,
  ).length;
  const cropFeedback = section.feedback.totalCropFeedback;
  const uncorrelatedFeedback = section.feedback.uncorrelatedCount;
  const complete =
    total > 0 && checks.every((check) => check.complete === total);
  const needsAttention =
    failedQc > 0 ||
    planAppliedMismatches > 0 ||
    cropFeedback > 0 ||
    uncorrelatedFeedback > 0;
  const status: CropValidationStatus = needsAttention
    ? "attention"
    : complete
      ? "ready"
      : "incomplete";
  const firstRiskSceneId =
    section.ranked.find((entry) => entry.tier !== "timeline")?.scene.sceneId ??
    null;

  return {
    status,
    checks,
    failedQc,
    planAppliedMismatches,
    fallbackOrRepair,
    cropFeedback,
    uncorrelatedFeedback,
    firstRiskSceneId,
  };
}
